import type { ReactNode } from 'react';
import { Select as S, Switch as Sw, ToggleGroup, Tooltip as T } from 'radix-ui';
import { m } from 'motion/react';
import { Check, ChevronDown } from 'lucide-react';
import { cx } from '@/lib/cx';
import { inputBase } from './Field';

export interface Option<V extends string = string> {
  value: V;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}

export function Select<V extends string>({
  value,
  onChange,
  options,
  placeholder = 'Elegí…',
  id,
  className,
  disabled,
  ...aria
}: {
  value: V | null | undefined;
  onChange: (v: V) => void;
  options: Option<V>[];
  placeholder?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}) {
  return (
    <S.Root value={value ?? undefined} onValueChange={(v) => onChange(v as V)} disabled={disabled}>
      <S.Trigger
        id={id}
        className={cx(inputBase, 'flex h-10 items-center justify-between gap-2 px-3 text-left data-placeholder:text-ink-300', className)}
        {...aria}
      >
        <S.Value placeholder={placeholder} />
        <S.Icon>
          <ChevronDown className="size-4 text-ink-400" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          className="z-[60] max-h-[min(360px,var(--radix-select-content-available-height))] min-w-(--radix-select-trigger-width) overflow-hidden rounded-xl border border-line bg-surface-0 shadow-e3 data-[state=open]:animate-rise"
        >
          <S.Viewport className="p-1">
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex cursor-pointer items-center gap-2 rounded-lg py-2 pr-8 pl-3 text-sm outline-none select-none data-disabled:opacity-40 data-highlighted:bg-gold-50 data-highlighted:text-ink-900"
              >
                <div className="min-w-0">
                  <S.ItemText>{o.label}</S.ItemText>
                  {o.hint && <div className="text-[12px] text-ink-500">{o.hint}</div>}
                </div>
                <S.ItemIndicator className="absolute right-2.5">
                  <Check className="size-4 text-gold-600" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
}) {
  const control = (
    <Sw.Root
      id={id}
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      className={cx(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors duration-200',
        checked ? 'gold-gradient border-gold-600/40' : 'border-line-strong bg-surface-3',
        'disabled:opacity-50',
      )}
    >
      <Sw.Thumb asChild>
        <m.span
          layout
          transition={{ type: 'spring', stiffness: 600, damping: 34 }}
          className={cx('block size-[18px] rounded-full bg-white shadow-e2', checked ? 'ml-[22px]' : 'ml-[2px]')}
        />
      </Sw.Thumb>
    </Sw.Root>
  );
  if (!label) return control;
  return (
    <label className={cx('flex cursor-pointer items-start gap-3', disabled && 'cursor-not-allowed opacity-60')}>
      <span className="mt-0.5">{control}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink-900">{label}</span>
        {description && <span className="block text-[12.5px] text-ink-500">{description}</span>}
      </span>
    </label>
  );
}

/** Segmented control with a gliding gold-edged indicator. */
export function Segmented<V extends string>({
  value,
  onChange,
  options,
  label,
  size = 'md',
  disabled,
}: {
  value: V;
  onChange: (v: V) => void;
  options: Option<V>[];
  label: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
}) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(v) => v && onChange(v as V)}
      aria-label={label}
      disabled={disabled}
      className="inline-flex rounded-[10px] border border-line bg-surface-2 p-0.5"
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <ToggleGroup.Item
            key={o.value}
            value={o.value}
            disabled={o.disabled}
            className={cx(
              'relative rounded-[8px] font-medium transition-colors disabled:opacity-40',
              size === 'sm' ? 'h-7 px-2.5 text-[12.5px]' : 'h-8 px-3.5 text-[13px]',
              active ? 'text-ink-900' : 'text-ink-500 hover:text-ink-900',
            )}
          >
            {active && (
              <m.span
                layoutId={`seg-${label}`}
                className="absolute inset-0 rounded-[8px] border border-gold-200 bg-surface-0 shadow-e1"
                transition={{ type: 'spring', stiffness: 500, damping: 38 }}
              />
            )}
            <span className="relative">{o.label}</span>
          </ToggleGroup.Item>
        );
      })}
    </ToggleGroup.Root>
  );
}

export function Tooltip({ content, children, side = 'top' }: { content: ReactNode; children: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <T.Root delayDuration={250}>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={6}
          className="z-[70] max-w-xs rounded-lg bg-ink-900 px-2.5 py-1.5 text-[12.5px] leading-snug text-white shadow-e3 data-[state=delayed-open]:animate-rise"
        >
          {content}
          <T.Arrow className="fill-ink-900" />
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}

export const TooltipProvider = T.Provider;
